/**
 * Durable handoff for an email-specific partner invitation.
 *
 * Registration links land on RegisterScreen even when the recipient already
 * has an Eleplan login. The invite must survive the hop to Login, Cognito MFA,
 * OAuth redirects, and an app restart so it can be accepted only after the
 * recipient authenticates. This store is intentionally separate from
 * pendingRegisterPrefill: form prefills are consumed once, while an invitation
 * is cleared only after the authenticated link endpoint accepts it (or returns
 * a terminal rejection).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import debug from './debug';

const STORAGE_KEY = '@eleplan/pending_partner_invite';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface PendingPartnerInvite {
  code: string;
  email?: string;
}

interface StoredPartnerInvite {
  invite: PendingPartnerInvite;
  savedAt: number;
}

const sanitize = (invite: PendingPartnerInvite | null | undefined): PendingPartnerInvite | null => {
  const code = typeof invite?.code === 'string' ? invite.code.trim() : '';
  if (!code || code.length > 256) return null;
  const email = typeof invite?.email === 'string' ? invite.email.trim().toLowerCase() : '';
  return { code, ...(email ? { email } : {}) };
};

export const setPendingPartnerInvite = async (
  invite: PendingPartnerInvite | null | undefined,
): Promise<void> => {
  const sanitized = sanitize(invite);
  if (!sanitized) return;
  try {
    // Screen-to-screen handoffs sometimes carry only the code. Preserve the
    // invited email already captured from the deep link when the code is the
    // same; otherwise a Register -> Login navigation can accidentally turn a
    // fully-bound invitation into a code-only record.
    const existing = !sanitized.email ? await getPendingPartnerInvite() : null;
    const merged = existing?.code === sanitized.code && existing.email
      ? { ...sanitized, email: existing.email }
      : sanitized;
    const payload: StoredPartnerInvite = { invite: merged, savedAt: Date.now() };
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    debug.log('storage', 'Pending partner invitation stored', { has_email: !!merged.email });
  } catch (err) {
    debug.warn('Failed to store pending partner invitation', err);
  }
};

export const getPendingPartnerInvite = async (): Promise<PendingPartnerInvite | null> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPartnerInvite;
    const invite = sanitize(parsed?.invite);
    if (!invite) {
      await AsyncStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > TTL_MS) {
      await AsyncStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return invite;
  } catch (err) {
    debug.warn('Failed to read pending partner invitation', err);
    return null;
  }
};

/**
 * Clear only the invitation the caller just handled. The optional code guard
 * prevents a slow response for an older link from deleting a newer link that
 * arrived while the request was in flight.
 */
export const clearPendingPartnerInvite = async (expectedCode?: string): Promise<void> => {
  try {
    if (expectedCode) {
      const current = await getPendingPartnerInvite();
      if (current && current.code !== expectedCode.trim()) return;
    }
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-critical. Expiry prevents stale invitations from living forever.
  }
};
